/**
 * RestoID — Identifier-first sign-in landing (`first_screen=identifier:sign_in`).
 * ---------------------------------------------------------------------------
 * Custom RestoID PRESENTATION on the PRISTINE upstream LOGIC of
 * `src/pages/IdentifierSignIn` + `src/components/IdentifierSignInForm` +
 * `src/components/PasswordSignInForm`:
 *   - which identifiers to ask for .. upstream `useIdentifierSignInMethods`
 *     (URL `identifier` params intersected with the SIE config, plus the
 *     `isPasswordOnly` split that decides whether the password sits on THIS
 *     screen or on the next one)
 *   - submit / flow routing ......... upstream `useOnSubmit` (SSO check on an
 *     e-mail, passkey-first, password-vs-code routing, send code, navigate)
 *   - password-only submit .......... upstream `usePasswordSignIn`
 *   - SSO type-ahead ................ upstream `useSingleSignOnWatch`
 *   - terms gate .................... upstream `useTerms` + `TermsAndPrivacyCheckbox`
 *   - captcha / forgot-password ..... upstream `CaptchaBox` / `ForgotPasswordLink`
 *
 * Deliberate differences from upstream, and why:
 *  - `FocusedAuthPageLayout` (its own h1 + description + footer links) is replaced
 *    by `FirstScreenLayout` + `BrandHead`, so this route looks like
 *    `custom/pages/SignIn` — it IS the same flow, entered through a different
 *    `first_screen`. The layout's `description` becomes the BrandHead subtitle and
 *    its `authOptionsLink` becomes a centred `LinkText`, exactly as
 *    `custom/pages/SingleSignOnLanding` already does for the SSO landing.
 *  - upstream's `SmartInputField` (one control that sniffs the identifier type as
 *    you type, 44px, floating label) is replaced by ONE explicit RestoID control,
 *    phone-first — the same choice `custom/pages/SignIn` makes, because
 *    `PhoneInput` is the design's identifier field and a tenant offering several
 *    identifiers still signs in with the one it declares first.
 *  - the layout's policy-gated terms FOOTER becomes the RestoID terms sentence
 *    (same wording and links as `custom/pages/SignIn`), gated by exactly the
 *    `footerTermsDisplayPolicies` upstream passed here — Automatic and
 *    ManualRegistrationOnly.
 *  - no `WebAuthnContextProvider`: upstream's IdentifierSignIn does not mount one
 *    either (`useOnSubmit` starts the passkey attempt itself), and mounting one
 *    would additionally fetch conditional-UI options this screen never uses.
 *  - upstream's `required` validation branch is unreachable here because the CTA
 *    is disabled until the field is filled (RestoID's convention); the `invalid`
 *    branch is kept verbatim.
 *  - upstream's `isPasskeyFlowProcessing` submit guard is dropped. It reads
 *    `WebAuthnContext`, and neither upstream's IdentifierSignIn nor this one mounts
 *    a `WebAuthnContextProvider`, so on THIS route the context default already
 *    pins it to `false` — the guard is dead code here, not a behaviour.
 *  - upstream's `useEffect(() => { if (!isValid) clearErrorMessage(); })` (wipe the
 *    server error once react-hook-form decides the form is invalid) has no
 *    equivalent: there is no `isValid`. The server error is cleared at the top of
 *    every submit instead, which is what `custom/pages/SignInPassword` already does.
 *  - upstream renders the server error in its own `ErrorMessage` block under the
 *    field; RestoID puts it in the field's own error slot (and the password field's
 *    slot on the password-only form, where a credentials error belongs).
 *
 * `IdentifierField` and `useIdentifierValue` are exported for
 * `custom/pages/IdentifierRegister`, `custom/pages/ResetPasswordLanding` and
 * `custom/pages/ForgotPassword`, which are the same landing in a different flow.
 * They live here rather than in `custom/components/restoid.tsx` because that module
 * is the design-source port; hoisting them is a possible cleanup, not a pending one.
 * The terms sentence they used to share HAS been hoisted, to
 * `custom/components/Terms`.
 * Swapped in for `./pages/IdentifierSignIn` via `src/App.tsx`.
 */
import { AgreeToTermsPolicy, type SignIn, SignInIdentifier, experience } from '@logto/schemas';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import SingleSignOnFormModeContextProvider from '@/Providers/SingleSignOnFormModeContextProvider';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import useOnSubmit from '@/components/IdentifierSignInForm/use-on-submit';
import CaptchaBox from '@/containers/CaptchaBox';
import ForgotPasswordLink from '@/containers/ForgotPasswordLink';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import usePasswordSignIn from '@/hooks/use-password-sign-in';
import usePrefilledIdentifier from '@/hooks/use-prefilled-identifier';
import { useForgotPasswordSettings } from '@/hooks/use-sie';
import useSingleSignOnWatch from '@/hooks/use-single-sign-on-watch';
import useTerms from '@/hooks/use-terms';
import useIdentifierSignInMethods from '@/pages/IdentifierSignIn/use-identifier-sign-in-methods';
import {
  getGeneralIdentifierErrorMessage,
  identifierInputDescriptionMap,
  validateIdentifierField,
} from '@/utils/form';

import { Terms } from '../components/Terms';
import {
  BrandHead,
  Button,
  C,
  FONT,
  Field,
  LinkText,
  PhoneInput,
  Stack,
  formatPhoneNat,
  phoneComplete,
  phoneToIdentifierValue,
} from '../components/restoid';
import { useCopy } from '../copy';

/* ---------------------------------------------------- the identifier control */

type IdentifierFieldProps = {
  identifier: SignInIdentifier;
  value: string;
  onChange: (next: string) => void;
  errorMessage?: string;
  autoFocus?: boolean;
  /** Upstream sets `off` on the register landing so the browser does not offer an
   *  existing account while the user is creating a new one. */
  autoComplete?: string;
};

/**
 * One RestoID identifier control: `PhoneInput` for a phone identifier, a plain
 * `Field` for e-mail / username — the same field behaviour `custom/pages/SignIn`
 * and `custom/pages/ForgotPassword` already give these flows.
 *
 * `PhoneInput` has no `autoFocus` prop (and the theme layer may not add one), so
 * a phone landing simply does not steal focus. That matches `custom/pages/SignIn`
 * and only ever costs the e-mail/username variants nothing.
 *
 * The error is announced by `Field` itself, which renders `errorMessage` in a
 * `role="alert"` div — the phone branch below carries its own, because it is
 * hand-rolled markup rather than a `Field`.
 */
export const IdentifierField = ({
  identifier,
  value,
  onChange,
  errorMessage,
  autoFocus = false,
  autoComplete,
}: IdentifierFieldProps) => {
  const { t } = useTranslation();
  const c = useCopy();

  if (identifier === SignInIdentifier.Phone) {
    return (
      <div>
        <label
          style={{ display: 'block', color: C.sub, font: `500 13px/1 ${FONT}`, marginBottom: 9 }}
        >
          {c('phoneLabel')}
        </label>
        <PhoneInput value={value} onChange={onChange} danger={Boolean(errorMessage)} />
        {errorMessage ? (
          <div
            role="alert"
            style={{ marginTop: 8, color: C.danger, font: `400 12.5px/1.45 ${FONT}` }}
          >
            {errorMessage}
          </div>
        ) : null}
      </div>
    );
  }

  const isEmail = identifier === SignInIdentifier.Email;

  return (
    <Field
      autoFocus={autoFocus}
      name="identifier"
      value={value}
      onChange={onChange}
      label={isEmail ? t('input.email') : t('input.username')}
      autoComplete={autoComplete ?? (isEmail ? 'email' : 'username')}
      inputMode={isEmail ? 'email' : 'text'}
      danger={Boolean(errorMessage)}
      errorMessage={errorMessage}
    />
  );
};

/**
 * The identifier value, pre-filled exactly as upstream pre-fills it
 * (`usePrefilledIdentifier` — the identifier cached on `UserInteractionContext`
 * this session, else the `login_hint` search parameter).
 *
 * `submitValue` is what the Experience API is handed: a phone becomes the
 * `7XXXXXXXXXX` digits `SmartInputField` would have composed, anything else is
 * trimmed (upstream's smart field trimmed on every keystroke).
 */
export const useIdentifierValue = (
  identifier: SignInIdentifier,
  enabledIdentifiers: SignInIdentifier[],
  options?: { isForgotPassword?: boolean }
) => {
  const isPhone = identifier === SignInIdentifier.Phone;
  const prefilled = usePrefilledIdentifier({
    enabledIdentifiers,
    isForgotPassword: options?.isForgotPassword,
  });

  const [value, setValue] = useState(() => {
    if (!isPhone) {
      return prefilled.value;
    }
    // A `login_hint` is pre-filled whatever its type, so adopt it only when it
    // really is a phone number — an e-mail hint would be mangled by the mask.
    return prefilled.type === SignInIdentifier.Phone || phoneComplete(prefilled.value)
      ? formatPhoneNat(prefilled.value)
      : '';
  });

  const trimmed = value.trim();

  return {
    value,
    setValue,
    /** Drives the CTA's disabled state — a phone must also be complete. */
    isFilled: isPhone ? phoneComplete(value) : trimmed.length > 0,
    submitValue: isPhone ? phoneToIdentifierValue(value) : trimmed,
  };
};

/* -------------------------------------------------------------------- forms */

type FormProps = {
  identifier: SignInIdentifier;
  identifiers: SignInIdentifier[];
  /** Upstream's `footerTermsDisplayPolicies` verdict for this policy. */
  showTerms: boolean;
};

/** Identifier only — upstream `IdentifierSignInForm`. */
const IdentifierSignInForm = ({
  identifier,
  identifiers,
  showTerms,
  signInMethods,
}: FormProps & { signInMethods: SignIn['methods'] }) => {
  const { t } = useTranslation();
  const c = useCopy();
  const { errorMessage, clearErrorMessage, onSubmit } = useOnSubmit(signInMethods);
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const { setIdentifierInputValue } = useContext(UserInteractionContext);
  const { value, setValue, isFilled, submitValue } = useIdentifierValue(identifier, identifiers);
  const [invalidMessage, setInvalidMessage] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  // Upstream watched the react-hook-form value; memoize so the debounced SSO
  // lookup is not restarted on every render.
  const identifierInput = useMemo(
    () => ({ type: identifier, value: submitValue }),
    [identifier, submitValue]
  );
  const { showSingleSignOnForm, navigateToSingleSignOn } = useSingleSignOnWatch(identifierInput);

  const isPhone = identifier === SignInIdentifier.Phone;
  const actionLabel = showSingleSignOnForm
    ? t('action.single_sign_on')
    : isPhone
      ? c('getCode')
      : t('action.sign_in');

  const submit = useCallback(async () => {
    if (!isFilled || submitting) {
      return;
    }
    clearErrorMessage();
    setInvalidMessage(undefined);

    if (validateIdentifierField(identifier, submitValue)) {
      setInvalidMessage(getGeneralIdentifierErrorMessage(identifiers, 'invalid'));
      return;
    }

    setIdentifierInputValue({ type: identifier, value: submitValue });

    if (showSingleSignOnForm) {
      await navigateToSingleSignOn();
      return;
    }

    // Match upstream: when the policy is Manual, confirm terms before signing in.
    if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(identifier, submitValue);
    } finally {
      setSubmitting(false);
    }
  }, [
    isFilled,
    submitting,
    clearErrorMessage,
    identifier,
    submitValue,
    identifiers,
    setIdentifierInputValue,
    showSingleSignOnForm,
    navigateToSingleSignOn,
    agreeToTermsPolicy,
    termsValidation,
    onSubmit,
  ]);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <Stack gap={20}>
        <IdentifierField
          identifier={identifier}
          value={value}
          errorMessage={invalidMessage ?? errorMessage}
          onChange={(next) => {
            setInvalidMessage(undefined);
            setValue(next);
          }}
        />
        {showSingleSignOnForm ? (
          <div style={{ textAlign: 'center', color: C.sub, font: `400 13px/1.5 ${FONT}` }}>
            {t('description.single_sign_on_enabled')}
          </div>
        ) : null}
        {/* Upstream shows the checkbox on this screen only for the Manual policy. */}
        {agreeToTermsPolicy === AgreeToTermsPolicy.Manual ? <TermsAndPrivacyCheckbox /> : null}
        <CaptchaBox />
        {/* No onClick: an onClick here would run the submit a second time per click. */}
        <Button
          htmlType="submit"
          title={actionLabel}
          isLoading={submitting}
          isDisabled={!isFilled}
        />
        {showTerms ? <Terms action={isPhone ? undefined : actionLabel} /> : null}
      </Stack>
    </form>
  );
};

/** Identifier + password on one screen — upstream `PasswordSignInForm`. */
const PasswordOnlySignInForm = ({ identifier, identifiers, showTerms }: FormProps) => {
  const { t } = useTranslation();
  const { errorMessage, clearErrorMessage, onSubmit } = usePasswordSignIn();
  const { isForgotPasswordEnabled } = useForgotPasswordSettings();
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const { setIdentifierInputValue } = useContext(UserInteractionContext);
  const { value, setValue, isFilled, submitValue } = useIdentifierValue(identifier, identifiers);
  const [password, setPassword] = useState('');
  const [invalidMessage, setInvalidMessage] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  const identifierInput = useMemo(
    () => ({ type: identifier, value: submitValue }),
    [identifier, submitValue]
  );
  const { showSingleSignOnForm, navigateToSingleSignOn } = useSingleSignOnWatch(identifierInput);

  const actionLabel = showSingleSignOnForm ? t('action.single_sign_on') : t('action.sign_in');
  // Upstream requires the password field (`error.password_required`); RestoID
  // disables the CTA instead, and the SSO branch submits without a password.
  const canSubmit = isFilled && (showSingleSignOnForm || password.length > 0);

  const submit = useCallback(async () => {
    if (!canSubmit || submitting) {
      return;
    }
    clearErrorMessage();
    setInvalidMessage(undefined);

    if (validateIdentifierField(identifier, submitValue)) {
      setInvalidMessage(getGeneralIdentifierErrorMessage(identifiers, 'invalid'));
      return;
    }

    setIdentifierInputValue({ type: identifier, value: submitValue });

    if (showSingleSignOnForm) {
      await navigateToSingleSignOn();
      return;
    }

    if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({ identifier: { type: identifier, value: submitValue }, password });
    } finally {
      setSubmitting(false);
    }
  }, [
    canSubmit,
    submitting,
    clearErrorMessage,
    identifier,
    submitValue,
    identifiers,
    setIdentifierInputValue,
    showSingleSignOnForm,
    navigateToSingleSignOn,
    agreeToTermsPolicy,
    termsValidation,
    onSubmit,
    password,
  ]);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <Stack gap={18}>
        <IdentifierField
          identifier={identifier}
          value={value}
          errorMessage={invalidMessage}
          onChange={(next) => {
            setInvalidMessage(undefined);
            setValue(next);
          }}
        />
        {showSingleSignOnForm ? (
          <div style={{ textAlign: 'center', color: C.sub, font: `400 13px/1.5 ${FONT}` }}>
            {t('description.single_sign_on_enabled')}
          </div>
        ) : (
          <Field
            password
            label={t('input.password')}
            value={password}
            onChange={setPassword}
            danger={Boolean(errorMessage)}
            errorMessage={errorMessage}
          />
        )}
        {isForgotPasswordEnabled && !showSingleSignOnForm ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ForgotPasswordLink identifier={identifier} value={submitValue} />
          </div>
        ) : null}
        {agreeToTermsPolicy === AgreeToTermsPolicy.Manual ? <TermsAndPrivacyCheckbox /> : null}
        <CaptchaBox />
        {/* No onClick: an onClick here would run the submit a second time per click. */}
        <Button
          htmlType="submit"
          title={actionLabel}
          isLoading={submitting}
          isDisabled={!canSubmit}
        />
        {showTerms ? <Terms action={actionLabel} /> : null}
      </Stack>
    </form>
  );
};

/* --------------------------------------------------------------- the screen */

const IdentifierSignIn = () => {
  const { t } = useTranslation();
  const navigate = useNavigateWithPreservedSearchParams();
  const { agreeToTermsPolicy } = useTerms();
  const { signInMethods, isPasswordOnly } = useIdentifierSignInMethods();

  const identifiers = useMemo(
    () => signInMethods.map(({ identifier }) => identifier),
    [signInMethods]
  );
  // Prefer phone when offered, so the production tenant is untouched; otherwise
  // take whatever the tenant put first (same rule as `custom/pages/SignIn`).
  const identifier = useMemo(
    () => identifiers.find((item) => item === SignInIdentifier.Phone) ?? identifiers[0],
    [identifiers]
  );

  // Fallback to the sign-in page if no sign-in methods are available.
  if (signInMethods.length === 0 || !identifier) {
    return <Navigate to={`/${experience.routes.signIn}`} />;
  }

  // Upstream's `footerTermsDisplayPolicies` for this page.
  const showTerms =
    agreeToTermsPolicy === AgreeToTermsPolicy.Automatic ||
    agreeToTermsPolicy === AgreeToTermsPolicy.ManualRegistrationOnly;

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'description.sign_in' }}>
      <SingleSignOnFormModeContextProvider>
        <Stack gap={22}>
          <BrandHead
            sub={t('description.identifier_sign_in_description', {
              types: identifiers.map((item) => t(identifierInputDescriptionMap[item])),
            })}
          />
          {isPasswordOnly ? (
            <PasswordOnlySignInForm
              identifier={identifier}
              identifiers={identifiers}
              showTerms={showTerms}
            />
          ) : (
            <IdentifierSignInForm
              identifier={identifier}
              identifiers={identifiers}
              showTerms={showTerms}
              signInMethods={signInMethods}
            />
          )}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <LinkText onClick={() => navigate(`/${experience.routes.signIn}`)}>
              {t('description.all_sign_in_options')}
            </LinkText>
          </div>
        </Stack>
      </SingleSignOnFormModeContextProvider>
    </FirstScreenLayout>
  );
};

export default IdentifierSignIn;
